import * as React from 'react'
import {
	Paper,
	Box,
	Stack,
	Avatar,
	Chip,
	IconButton,
	Typography,
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'

import DataGridTable from './DataGridTable'
import ListToolbar from './ListToolBar'
import FilterPopover from './FilterPopover'
import KebabMenu from './KebabMenu'
import StatusBtn from '../atoms/status/StatusBtn'
import { fetchAllGuestsForExport } from '../../features/guestListSlice.js'
import { toastHelper } from '../../util/toastHelper.js'

const ARCHIVE_GUEST_TAG = 'Archive'
const PARTICIPATION_STATUSES = [
	'Draft',
	'Invited',
	'Accepted',
	'Declined',
	'Cancelled',
]
const SHOW_ARCHIVED_OPTION = 'Show archived'

/** Toggleable columns (Name + actions always shown). */
const COLUMN_OPTIONS = [
	{ id: 'roleInWedding', label: 'Role in Wedding', defaultVisible: true },
	{ id: 'email', label: 'Email', defaultVisible: true },
	{ id: 'phone', label: 'Phone', defaultVisible: false },
	{ id: 'guestTags', label: 'Guest Tags', defaultVisible: true },
	{ id: 'participation', label: 'Participation', defaultVisible: true },
	{
		id: 'additionalGuestAllowance',
		label: 'Additional guest allowance',
		defaultVisible: true,
	},
	{ id: 'dietary', label: 'Dietary restrictions', defaultVisible: false },
	{ id: 'notes', label: 'Notes', defaultVisible: false },
	{ id: 'preferredName', label: 'Preferred name', defaultVisible: false },
	{ id: 'linked', label: 'Linked account', defaultVisible: false },
]

function defaultVisibleColumns() {
	return Object.fromEntries(
		COLUMN_OPTIONS.map((c) => [c.id, c.defaultVisible])
	)
}

function participationVariant(status) {
	switch (status) {
		case 'Accepted':
			return 'active'
		case 'Invited':
			return 'pending'
		case 'Draft':
			return 'inprogress'
		case 'Declined':
		case 'Cancelled':
			return 'closed'
		default:
			return 'active'
	}
}

function formatDietary(guest) {
	const restrictions = guest?.dietary?.restrictions || []
	const notes = guest?.dietary?.notes || ''
	const parts = []
	if (restrictions.length) parts.push(restrictions.join('; '))
	if (notes) parts.push(notes)
	return parts.join(' — ') || ''
}

function formatParticipation(guest) {
	return (guest?.eventParticipation || [])
		.map((p) => `${p.eventTag}: ${p.status}`)
		.join('; ')
}

function formatSeating(guest) {
	return (guest?.seating || [])
		.map((s) => `${s.label}: ${s.assignment}`)
		.join('; ')
}

function guestMatchesClientFilters(guest, filters) {
	const {
		includeArchived,
		roleFilter,
		guestTagFilter,
		eventTagFilter,
		participationStatusFilter,
	} = filters
	const tags = guest.guestTags ?? []
	const isArchived = tags.includes(ARCHIVE_GUEST_TAG)
	if (!includeArchived && isArchived) return false

	if (roleFilter.length && !roleFilter.includes(guest.roleInWedding)) {
		return false
	}

	if (guestTagFilter.length && !guestTagFilter.some((t) => tags.includes(t))) {
		return false
	}

	const participations = guest.eventParticipation ?? []
	if (eventTagFilter.length || participationStatusFilter.length) {
		if (eventTagFilter.length && participationStatusFilter.length) {
			const matches = eventTagFilter.some((eventTag) => {
				const row = participations.find((p) => p.eventTag === eventTag)
				return row && participationStatusFilter.includes(row.status)
			})
			if (!matches) return false
		} else if (eventTagFilter.length) {
			if (
				!eventTagFilter.some((eventTag) =>
					participations.some((p) => p.eventTag === eventTag)
				)
			) {
				return false
			}
		} else if (participationStatusFilter.length) {
			if (
				!participations.some((p) =>
					participationStatusFilter.includes(p.status)
				)
			) {
				return false
			}
		}
	}

	return true
}

function buildGuestRow(guest) {
	const name =
		guest.preferredName ||
		[guest.firstName, guest.lastName].filter(Boolean).join(' ').trim() ||
		guest.email ||
		'Unknown'
	const tags = guest.guestTags ?? []
	const participations = guest.eventParticipation ?? []

	return {
		id: guest._id,
		name,
		preferredName: guest.preferredName || '',
		avatar: guest.profilePic || '',
		roleInWedding: guest.roleInWedding || null,
		email: guest.email || '',
		phone: guest.phone || '',
		guestTags: tags,
		participationSummary: formatParticipation(guest),
		participations,
		additionalGuestAllowance: guest.additionalGuestAllowance ?? 0,
		dietarySummary: formatDietary(guest),
		notes: guest.notes || '',
		hasUserId: Boolean(guest.userId),
		isArchived: tags.includes(ARCHIVE_GUEST_TAG),
		_raw: guest,
	}
}

export default function GuestListDataGrid({
	guests = [],
	total = 0,
	page = 1,
	pageSize = 50,
	kazooId,
	eventTags = [],
	guestTags = [],
	customFieldDefinitions = [],
	includeArchived = false,
	onToggleIncludeArchived,
	searchValue = '',
	onSearchChange,
	onEdit,
	onDelete,
	onArchive,
	loading = false,
	onPaginationModelChange,
}) {
	const rows = React.useMemo(
		() => (guests || []).map(buildGuestRow),
		[guests]
	)

	const [roleFilter, setRoleFilter] = React.useState([])
	const [guestTagFilter, setGuestTagFilter] = React.useState([])
	const [eventTagFilter, setEventTagFilter] = React.useState([])
	const [participationStatusFilter, setParticipationStatusFilter] =
		React.useState([])
	const [visibleColumns, setVisibleColumns] = React.useState(
		defaultVisibleColumns
	)
	const [exporting, setExporting] = React.useState(false)

	const availableRoles = React.useMemo(() => {
		const s = new Set(rows.map((r) => r.roleInWedding).filter(Boolean))
		return Array.from(s).sort()
	}, [rows])

	const availableGuestTags = React.useMemo(() => {
		const s = new Set(guestTags)
		rows.forEach((r) => r.guestTags.forEach((t) => s.add(t)))
		return Array.from(s).sort()
	}, [rows, guestTags])

	const availableEventTags = React.useMemo(() => {
		const s = new Set(eventTags)
		rows.forEach((r) => r.participations.forEach((p) => s.add(p.eventTag)))
		return Array.from(s).sort()
	}, [rows, eventTags])

	const clientFilterState = React.useMemo(
		() => ({
			includeArchived,
			roleFilter,
			guestTagFilter,
			eventTagFilter,
			participationStatusFilter,
		}),
		[
			includeArchived,
			roleFilter,
			guestTagFilter,
			eventTagFilter,
			participationStatusFilter,
		]
	)

	const filteredRows = React.useMemo(() => {
		return rows.filter((r) =>
			guestMatchesClientFilters(r._raw, clientFilterState)
		)
	}, [rows, clientFilterState])

	const chips = [
		...(includeArchived
			? [
					{
						key: 'archived',
						label: 'Show archived',
						onDelete: () => onToggleIncludeArchived?.(false),
					},
				]
			: []),
		...roleFilter.map((r) => ({
			key: `role-${r}`,
			label: `Role ${r}`,
			onDelete: () => setRoleFilter((prev) => prev.filter((x) => x !== r)),
		})),
		...guestTagFilter.map((t) => ({
			key: `guest-tag-${t}`,
			label: `Tag ${t}`,
			onDelete: () =>
				setGuestTagFilter((prev) => prev.filter((x) => x !== t)),
		})),
		...eventTagFilter.map((t) => ({
			key: `event-tag-${t}`,
			label: `Event ${t}`,
			onDelete: () =>
				setEventTagFilter((prev) => prev.filter((x) => x !== t)),
		})),
		...participationStatusFilter.map((s) => ({
			key: `participation-${s}`,
			label: `Status ${s}`,
			onDelete: () =>
				setParticipationStatusFilter((prev) =>
					prev.filter((x) => x !== s)
				),
		})),
	]

	const clearFilters = () => {
		setRoleFilter([])
		setGuestTagFilter([])
		setEventTagFilter([])
		setParticipationStatusFilter([])
		onToggleIncludeArchived?.(false)
	}

	const [filterAnchor, setFilterAnchor] = React.useState(null)
	const openFilter = (e) => setFilterAnchor(e.currentTarget)
	const closeFilter = () => setFilterAnchor(null)

	const [columnsAnchor, setColumnsAnchor] = React.useState(null)
	const openColumns = (e) => setColumnsAnchor(e.currentTarget)
	const closeColumns = () => setColumnsAnchor(null)

	const toggleColumn = (id, checked) => {
		setVisibleColumns((prev) => ({ ...prev, [id]: checked }))
	}

	const [menuAnchor, setMenuAnchor] = React.useState(null)
	const [menuRow, setMenuRow] = React.useState(null)
	const openMenu = (e, row) => {
		e.stopPropagation()
		setMenuAnchor(e.currentTarget)
		setMenuRow(row)
	}
	const closeMenu = () => {
		setMenuAnchor(null)
		setMenuRow(null)
	}

	const exportCSV = async () => {
		if (!kazooId || exporting) return
		setExporting(true)
		try {
			const allGuests = await fetchAllGuestsForExport({
				kazooId,
				includeArchived,
				q: searchValue.trim(),
			})
			const matched = allGuests.filter((g) =>
				guestMatchesClientFilters(g, clientFilterState)
			)

			const customHeaders = (customFieldDefinitions || []).map(
				(d) => `Custom: ${d.name}`
			)
			const header = [
				'First Name',
				'Last Name',
				'Preferred Name',
				'Pronouns',
				'Email',
				'Phone',
				'Role in Wedding',
				'Guest Tags',
				'Event Participation',
				'Dietary Restrictions',
				'Dietary Notes',
				'Additional Guest Allowance',
				'Seating',
				'Notes',
				'Linked Account',
				...customHeaders,
			]

			const data = matched.map((g) => {
				const customVals = (customFieldDefinitions || []).map((def) => {
					const entry = (g.customFieldValues || []).find(
						(v) => String(v.fieldId) === String(def._id)
					)
					if (entry == null || entry.value == null) return ''
					if (Array.isArray(entry.value)) return entry.value.join('; ')
					return String(entry.value)
				})
				return [
					g.firstName || '',
					g.lastName || '',
					g.preferredName || '',
					g.pronouns || '',
					g.email || '',
					g.phone || '',
					g.roleInWedding || '',
					(g.guestTags || []).join('; '),
					formatParticipation(g),
					(g.dietary?.restrictions || []).join('; '),
					g.dietary?.notes || '',
					g.additionalGuestAllowance ?? 0,
					formatSeating(g),
					g.notes || '',
					g.userId ? 'Yes' : 'No',
					...customVals,
				]
			})

			const csv = [header, ...data]
				.map((r) => r.map(esc).join(','))
				.join('\n')
			const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
			const url = URL.createObjectURL(blob)
			const a = document.createElement('a')
			a.href = url
			a.download = 'guest-list.csv'
			a.click()
			URL.revokeObjectURL(url)
			toastHelper(
				`Exported ${matched.length} guest${matched.length === 1 ? '' : 's'}`,
				'success'
			)
		} catch (err) {
			toastHelper(err.message || 'Export failed', 'error')
		} finally {
			setExporting(false)
		}
	}

	const tagChipSx = {
		'&.MuiChip-root': {
			bgcolor: '#F2F6F8',
			borderRadius: '4px',
			height: '22px',
		},
		'& .MuiChip-label': {
			fontSize: '11px',
			fontFamily: 'Inter',
			fontWeight: '400',
			color: '#313131',
			padding: '0 6px',
		},
	}

	const columns = React.useMemo(() => {
		const cols = [
			{
				field: 'name',
				headerName: 'Name',
				flex: 1.6,
				minWidth: 220,
				sortable: true,
				renderCell: (params) => {
					const { avatar, name } = params.row
					return (
						<Stack
							direction="row"
							spacing={1.5}
							alignItems="center"
							sx={{ height: '100%' }}
						>
							<Avatar
								src={
									avatar ||
									'https://static.vecteezy.com/system/resources/previews/020/911/740/non_2x/user-profile-icon-profile-avatar-user-icon-male-icon-face-icon-profile-icon-free-png.png'
								}
								sx={{ width: 32, height: 32 }}
							/>
							<Box component="span" sx={{ fontWeight: 600 }}>
								{name}
							</Box>
						</Stack>
					)
				},
			},
		]

		if (visibleColumns.preferredName) {
			cols.push({
				field: 'preferredName',
				headerName: 'Preferred name',
				flex: 1,
				minWidth: 140,
				sortable: true,
				renderCell: (params) => <p>{params.value || '—'}</p>,
			})
		}

		if (visibleColumns.roleInWedding) {
			cols.push({
				field: 'roleInWedding',
				headerName: 'Role in Wedding',
				flex: 1,
				minWidth: 140,
				sortable: true,
				renderCell: (params) => <p>{params.value ?? '—'}</p>,
			})
		}

		if (visibleColumns.email) {
			cols.push({
				field: 'email',
				headerName: 'Email',
				flex: 1.2,
				minWidth: 180,
				sortable: true,
				renderCell: (params) => <p>{params.value || '—'}</p>,
			})
		}

		if (visibleColumns.phone) {
			cols.push({
				field: 'phone',
				headerName: 'Phone',
				flex: 1,
				minWidth: 130,
				sortable: true,
				renderCell: (params) => <p>{params.value || '—'}</p>,
			})
		}

		if (visibleColumns.guestTags) {
			cols.push({
				field: 'guestTags',
				headerName: 'Tags',
				flex: 1.2,
				minWidth: 160,
				sortable: false,
				renderCell: (params) => {
					const tags = params.value ?? []
					if (!tags.length) return <p>—</p>
					return (
						<Stack
							direction="row"
							spacing={0.5}
							flexWrap="wrap"
							useFlexGap
							alignItems="center"
							sx={{ height: '100%', py: 0.5 }}
						>
							{tags.map((tag) => (
								<Chip
									key={tag}
									label={tag}
									size="small"
									sx={tagChipSx}
								/>
							))}
						</Stack>
					)
				},
			})
		}

		if (visibleColumns.participation) {
			cols.push({
				field: 'participationSummary',
				headerName: 'Participation',
				flex: 1.4,
				minWidth: 200,
				sortable: false,
				renderCell: (params) => {
					const { participations } = params.row
					if (!participations?.length) return <p>—</p>
					return (
						<Stack
							direction="row"
							spacing={0.5}
							flexWrap="wrap"
							useFlexGap
							alignItems="center"
							sx={{ height: '100%', py: 0.5 }}
						>
							{participations.map((p) => (
								<StatusBtn
									key={`${p.eventTag}-${p.status}`}
									label={`${p.eventTag}: ${p.status}`}
									variant={participationVariant(p.status)}
								/>
							))}
						</Stack>
					)
				},
			})
		}

		if (visibleColumns.additionalGuestAllowance) {
			cols.push({
				field: 'additionalGuestAllowance',
				headerName: 'Allowance',
				flex: 0.7,
				minWidth: 110,
				sortable: true,
				renderCell: (params) => (
					<p>{params.value == null ? '—' : params.value}</p>
				),
			})
		}

		if (visibleColumns.dietary) {
			cols.push({
				field: 'dietarySummary',
				headerName: 'Dietary',
				flex: 1.2,
				minWidth: 160,
				sortable: false,
				renderCell: (params) => <p>{params.value || '—'}</p>,
			})
		}

		if (visibleColumns.notes) {
			cols.push({
				field: 'notes',
				headerName: 'Notes',
				flex: 1.2,
				minWidth: 160,
				sortable: false,
				renderCell: (params) => (
					<Typography
						variant="body2"
						sx={{
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
							maxWidth: '100%',
						}}
					>
						{params.value || '—'}
					</Typography>
				),
			})
		}

		if (visibleColumns.linked) {
			cols.push({
				field: 'hasUserId',
				headerName: 'Linked',
				flex: 0.6,
				minWidth: 100,
				sortable: true,
				renderCell: (params) =>
					params.value ? (
						<Chip
							label="Linked"
							size="small"
							variant="outlined"
							sx={tagChipSx}
						/>
					) : (
						<Box component="span" sx={{ color: '#8692A4' }}>
							—
						</Box>
					),
			})
		}

		cols.push({
			field: 'menu',
			headerName: '',
			sortable: false,
			filterable: false,
			width: 64,
			align: 'right',
			renderCell: (params) => (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						width: '100%',
						height: '100%',
					}}
				>
					<IconButton
						data-cy="guest-list-kebab"
						size="small"
						onClick={(e) => openMenu(e, params.row)}
						sx={{ p: 0.5 }}
					>
						<MoreVertIcon />
					</IconButton>
				</Box>
			),
		})

		return cols
	}, [visibleColumns])

	const kebabItems = React.useMemo(() => {
		const items = [
			{
				key: 'edit',
				label: 'Edit',
				icon: <img src="/images/edit-icon.svg" alt="edit" />,
				onClick: () => onEdit?.(menuRow?._raw),
			},
		]

		if (menuRow && !menuRow.isArchived) {
			items.push({
				key: 'archive',
				label: 'Archive',
				icon: <ArchiveOutlinedIcon fontSize="small" />,
				onClick: () => onArchive?.(menuRow?._raw),
			})
		}

		items.push({
			key: 'delete',
			label: 'Delete',
			icon: <DeleteOutlineIcon fontSize="small" />,
			onClick: () => onDelete?.(menuRow?._raw),
			color: 'error.main',
		})

		return items
	}, [menuRow, onEdit, onArchive, onDelete])

	const columnOptionLabels = COLUMN_OPTIONS.map((c) => c.label)
	const selectedColumnLabels = COLUMN_OPTIONS.filter(
		(c) => visibleColumns[c.id]
	).map((c) => c.label)

	return (
		<Paper
			data-cy="guest-list-grid"
			sx={{ p: 2, width: '100%', maxWidth: 'none' }}
		>
			<Box>
				<ListToolbar
					title="Guest List"
					searchPlaceholder="Search guests"
					searchValue={searchValue}
					onSearchChange={onSearchChange}
					searchDataCy="guest-list-search"
					chips={chips}
					onClearFilters={clearFilters}
					onOpenFilter={openFilter}
					onOpenColumns={openColumns}
					onExport={exportCSV}
					isFiltered
					isColumns
					isExported
					columnsDataCy="guest-list-columns-btn"
				/>

				<FilterPopover
					anchorEl={filterAnchor}
					open={Boolean(filterAnchor)}
					onClose={closeFilter}
					onClear={clearFilters}
					onApply={closeFilter}
					sections={[
						{
							id: 'archived',
							title: 'Archived',
							options: [SHOW_ARCHIVED_OPTION],
							selected: includeArchived
								? [SHOW_ARCHIVED_OPTION]
								: [],
							onToggle: (_val, checked) =>
								onToggleIncludeArchived?.(checked),
						},
						{
							id: 'roleInWedding',
							title: 'Role in Wedding',
							options: availableRoles,
							selected: roleFilter,
							onToggle: (val, checked) =>
								setRoleFilter((prev) =>
									checked
										? [...prev, val]
										: prev.filter((x) => x !== val)
								),
						},
						{
							id: 'guestTags',
							title: 'Guest Tags',
							options: availableGuestTags,
							selected: guestTagFilter,
							onToggle: (val, checked) =>
								setGuestTagFilter((prev) =>
									checked
										? [...prev, val]
										: prev.filter((x) => x !== val)
								),
						},
						{
							id: 'eventTags',
							title: 'Event Tag',
							options: availableEventTags,
							selected: eventTagFilter,
							onToggle: (val, checked) =>
								setEventTagFilter((prev) =>
									checked
										? [...prev, val]
										: prev.filter((x) => x !== val)
								),
						},
						{
							id: 'participationStatus',
							title: 'Participation Status',
							options: PARTICIPATION_STATUSES,
							selected: participationStatusFilter,
							onToggle: (val, checked) =>
								setParticipationStatusFilter((prev) =>
									checked
										? [...prev, val]
										: prev.filter((x) => x !== val)
								),
						},
					]}
				/>

				<FilterPopover
					anchorEl={columnsAnchor}
					open={Boolean(columnsAnchor)}
					onClose={closeColumns}
					onClear={() => setVisibleColumns(defaultVisibleColumns())}
					onApply={closeColumns}
					title="Columns"
					clearLabel="Reset"
					sections={[
						{
							id: 'columns',
							title: 'Show columns',
							options: columnOptionLabels,
							selected: selectedColumnLabels,
							onToggle: (label, checked) => {
								const opt = COLUMN_OPTIONS.find(
									(c) => c.label === label
								)
								if (opt) toggleColumn(opt.id, checked)
							},
						},
					]}
				/>

				<DataGridTable
					rows={filteredRows}
					columns={columns}
					loading={loading || exporting}
					checkboxSelection={false}
					paginationMode="server"
					rowCount={total}
					pageSizeOptions={[25, 50, 100, 200]}
					paginationModel={{
						page: Math.max(0, (page || 1) - 1),
						pageSize: pageSize || 50,
					}}
					onPaginationModelChange={onPaginationModelChange}
					data-cy="guest-list-table"
				/>

				<KebabMenu
					anchorEl={menuAnchor}
					open={Boolean(menuAnchor)}
					onClose={closeMenu}
					items={kebabItems}
				/>
			</Box>
		</Paper>
	)
}

function esc(s) {
	const str = String(s ?? '')
	return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str
}
